import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import {
  ParamSearchFilter,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import {
  useGetConversation,
  useGetConversationsInfinite,
} from "@/requests/gen/react-query/text-message";
import { useEffect, useRef, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { TextMessageWithPatients } from "@/requests/gen/react-query/fastAPI.schemas";
import TextMessaging from "@/components/universal/text-messaging";
import { Separator } from "@/components/ui/separator";
import { formatUserInitials, formatUserName } from "@/lib/format-user-name";
import { User } from "lucide-react";
import { useQueryState, useQueryStates } from "nuqs";
import { format, isToday } from "date-fns";

export const Route = createFileRoute("/_private/_patient-360/texting")({
  component: TextingPage,
});

function TextingPage() {
  const [queryStates] = useQueryStates(dataTableSearchParsers);
  const [selectedPhone, setSelectedPhone] = useQueryState("phone", {
    history: "push",
  });
  const [selectedConversation, setSelectedConversation] =
    useState<TextMessageWithPatients | null>(null);

  const {
    fetchNextPage,
    isFetching,
    data: conversations,
    refetch: refetchConversations,
  } = useGetConversationsInfinite(
    {
      search: queryStates.search,
      limit: 100,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  // Setup infinite scrolling for the conversation list
  const viewportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const onScroll = () => {
      const nearBottom =
        viewport.scrollTop + viewport.clientHeight >=
        viewport.scrollHeight - 250;
      if (nearBottom && !isFetching) {
        fetchNextPage();
      }
    };

    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", onScroll);
  }, [fetchNextPage, isFetching]);

  // Fetch the selected conversation history
  const selectedConversationHistory = useGetConversation(
    {
      patient_phone: selectedConversation?.patient_phone || "",
      patient_ids: selectedConversation?.text_message_patients.map(
        (tmp) => tmp.patient_id,
      ),
    },
    {
      query: {
        enabled: !!selectedConversation,
      },
    },
  );

  // Automatically set selectedConversation based on URL phone param
  useEffect(() => {
    const match = conversations?.pages
      .flat()
      .find((conv) => conv.patient_phone === selectedPhone);
    setSelectedConversation(match || null);
  }, [selectedPhone, conversations]);

  // When a conversation is clicked
  const handleSelectConversation = (conv: TextMessageWithPatients) => {
    setSelectedPhone(conv.patient_phone);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      <title>Texting - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Texting", link: { to: "/texting" } }]} />
      <Card className="flex-1 flex flex-col min-h-0">
        <CardContent className="flex-1 flex px-0 min-h-0">
          {/* Left Column: Conversation List */}
          <div
            className={cn(
              "w-full md:w-80 flex flex-col gap-2 md:border-r pr-1 pl-6",
              selectedConversation ? "hidden md:flex" : "flex h-full",
            )}
          >
            <div className="pr-3">
              <ParamSearchFilter isFetching={isFetching} className="w-full" />
            </div>

            <ScrollArea
              className="flex-1 min-h-0 pr-3"
              viewportRef={viewportRef}
            >
              {conversations?.pages.flat().map((text_message) => {
                const isSender = text_message.sent_by !== "patient";
                const isSelected =
                  selectedConversation?.text_message_id ===
                  text_message.text_message_id;

                return (
                  <div key={text_message.text_message_id}>
                    <button
                      onClick={() => handleSelectConversation(text_message)}
                      className={cn(
                        "w-full p-4 text-left hover:bg-muted/70 transition-colors flex items-center gap-3 rounded-xl",
                        isSelected && "bg-muted",
                      )}
                    >
                      <div className="relative space-y-2">
                        {!isSender && !text_message.read_at && (
                          <div className="size-2 rounded-full bg-primary/80 absolute -left-2 -top-2" />
                        )}
                        {text_message.text_message_patients.length > 0 ? (
                          text_message.text_message_patients.map(
                            (text_message_patient) => (
                              <Avatar
                                className="size-10"
                                key={text_message_patient.patient_id}
                              >
                                <AvatarFallback className="bg-muted text-xs">
                                  {formatUserInitials(
                                    text_message_patient.patient,
                                  )}
                                </AvatarFallback>
                              </Avatar>
                            ),
                          )
                        ) : (
                          <Avatar className="size-10">
                            <AvatarFallback className="bg-muted text-xs">
                              <User className="size-4" />
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline">
                          {text_message.text_message_patients.length > 0 ? (
                            <div className="flex flex-col">
                              {text_message.text_message_patients.map(
                                (text_message_patient) => (
                                  <div
                                    key={text_message_patient.patient_id}
                                    className="font-semibold text-sm mb-1"
                                  >
                                    {formatUserName(
                                      text_message_patient.patient,
                                    )}
                                  </div>
                                ),
                              )}
                            </div>
                          ) : (
                            <div className="font-semibold text-sm mb-1">
                              {text_message.patient_phone}
                            </div>
                          )}
                          <p className="text-muted-foreground text-sm">
                            {isToday(new Date(text_message.created_at))
                              ? format(
                                  new Date(text_message.created_at),
                                  "hh:mm a",
                                )
                              : format(
                                  new Date(text_message.created_at),
                                  "MM-dd-yyyy",
                                )}
                          </p>
                        </div>
                        <div className="text-sm text-muted-foreground line-clamp-1">
                          {text_message.message}
                        </div>
                      </div>
                    </button>
                    <Separator className="my-2 mx-4 bg-border/50 last:hidden" />
                  </div>
                );
              })}
            </ScrollArea>
          </div>

          {/* Right Column: Messaging Area */}
          <div
            className={cn(
              "flex-1 flex flex-col min-h-0 md:min-w-76",
              !selectedConversation ? "hidden md:flex" : "flex h-full",
            )}
          >
            <TextMessaging
              patient_phone={selectedConversation?.patient_phone}
              patients={selectedConversation?.text_message_patients.map(
                (text_message_patient) => text_message_patient.patient,
              )}
              practice={selectedConversation?.practice}
              conversation={selectedConversationHistory.data}
              texting_enabled={
                selectedConversation?.text_message_patients.every(
                  (patient) => patient.patient.texting_unsubscribed_at === null,
                ) || false
              }
              refetch={() => {
                refetchConversations();
                selectedConversationHistory.refetch();
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
